import React, { useState } from 'react';
import { useQuote } from '../context/QuoteContext';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import './Admin.css';

const STATUSES = ['Pending', 'In Review', 'Approved', 'Rejected'];

function StatusBadge({ status }) {
  return <span className={`status-badge ${status.toLowerCase().replace(' ','-')}`}>{status}</span>;
}

export default function Admin() {
  const { quotes, updateQuoteStatus } = useQuote();
  const { toasts, toast } = useToast();
  const [filter, setFilter]   = useState('All');
  const [search, setSearch]   = useState('');
  const [selected, setSelected] = useState(null);

  const filtered = quotes.filter(q => {
    const matchStatus = filter === 'All' || q.status === filter;
    const matchSearch = !search || q.name.toLowerCase().includes(search.toLowerCase()) || q.email.toLowerCase().includes(search.toLowerCase()) || q.service.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const handleStatus = (id, status) => {
    updateQuoteStatus(id, status);
    toast(`Quote updated to "${status}"`, 'success');
    if (selected?.id === id) setSelected(q => ({ ...q, status }));
  };

  return (
    <>
      <Toast toasts={toasts} />
      <div className="admin-page">
        <div className="container">
          <div className="admin-header">
            <div>
              <h1>Admin Panel</h1>
              <p className="admin-sub">Manage all quote requests and client enquiries.</p>
            </div>
            <div className="admin-summary">
              {['All',...STATUSES].map(s => (
                <div key={s} className={`as-chip${filter === s ? ' active' : ''}`} onClick={() => setFilter(s)}>
                  <span className="as-count">{s === 'All' ? quotes.length : quotes.filter(q => q.status === s).length}</span>
                  <span className="as-label">{s}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="admin-search">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍  Search by name, email, or service…" className="admin-search-input" />
          </div>

          <div className="admin-layout">
            {/* List */}
            <div className="admin-list">
              {filtered.length === 0 ? (
                <div className="admin-empty"><p>No quotes match your filters.</p></div>
              ) : filtered.map(q => (
                <div key={q.id} className={`admin-row${selected?.id === q.id ? ' active' : ''}`} onClick={() => setSelected(q)}>
                  <div className="ar-top">
                    <code className="quote-id">{q.id}</code>
                    <StatusBadge status={q.status} />
                  </div>
                  <div className="ar-name">{q.name}</div>
                  <div className="ar-meta">{q.service} · {q.email}</div>
                  <div className="ar-date">{new Date(q.submittedAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</div>
                </div>
              ))}
            </div>

            {/* Detail */}
            <div className="admin-detail">
              {!selected ? (
                <div className="ad-empty"><div className="ad-empty-icon">📋</div><p>Select a quote to view details</p></div>
              ) : (
                <div className="ad-content">
                  <div className="ad-top">
                    <div>
                      <code className="quote-id">{selected.id}</code>
                      <h2>{selected.name}</h2>
                      <p className="ad-email">{selected.email} {selected.phone && `· ${selected.phone}`}</p>
                    </div>
                    <StatusBadge status={selected.status} />
                  </div>

                  <div className="ad-grid">
                    <div className="ad-field"><div className="adf-label">Service</div><div className="adf-val">{selected.service}</div></div>
                    <div className="ad-field"><div className="adf-label">Budget</div><div className="adf-val">{selected.budget || '—'}</div></div>
                    <div className="ad-field"><div className="adf-label">Timeline</div><div className="adf-val">{selected.timeline || '—'}</div></div>
                    <div className="ad-field"><div className="adf-label">Company</div><div className="adf-val">{selected.company || '—'}</div></div>
                    <div className="ad-field full"><div className="adf-label">Submitted</div><div className="adf-val">{new Date(selected.submittedAt).toLocaleString('en-IN')}</div></div>
                    <div className="ad-field full"><div className="adf-label">Project Brief</div><div className="adf-val ad-message">{selected.message}</div></div>
                  </div>

                  <div className="ad-actions">
                    <p className="ad-actions-label">Update status:</p>
                    <div className="ad-status-btns">
                      {STATUSES.map(s => (
                        <button key={s} className={`ad-status-btn ${s.toLowerCase().replace(' ','-')}${selected.status === s ? ' current' : ''}`}
                          onClick={() => handleStatus(selected.id, s)} disabled={selected.status === s}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="ad-contact">
                    <p className="ad-actions-label">Contact client:</p>
                    <div className="ad-contact-btns">
                      <a href={`mailto:${selected.email}?subject=Re: Your NextGenHub Quote ${selected.id}`} className="btn-ink" style={{ fontSize: 13, padding: '8px 16px' }}>✉ Email</a>
                      {selected.phone && <a href={`https://wa.me/${selected.phone.replace(/\D/g,'')}`} className="btn-out" style={{ fontSize: 13, padding: '8px 16px' }} target="_blank" rel="noopener noreferrer">💬 WhatsApp</a>}
                      {selected.phone && <a href={`tel:${selected.phone}`} className="btn-out" style={{ fontSize: 13, padding: '8px 16px' }}>📞 Call</a>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
